import { useRef, useState } from 'react';
import { getUrlStringFromObjectEntries } from '../../utils';
import { SERVICEABLE_URLS_ON_PAGE } from '../../settings';
import useBatchUrlAnalysisProcessesPolling from '../../process/hooks/useBatchUrlAnalysisProcessesPolling';
import { ServiceableUrl } from '../../serviceableUrls/types';
import { useLazyFetchAffectedUrlsQuery } from '../service';
import { AffectedClustersLastEvaluatedKey } from '../types';

type Props = {
    websiteGuid: string;
    clusterGuid: string;
    seoTestKey: string;
    clusterLastEvaluatedKey: AffectedClustersLastEvaluatedKey | null;
};

export default function useFetchAffectedClusterUrlsPage({ websiteGuid, clusterGuid, seoTestKey, clusterLastEvaluatedKey }: Props) {
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState<AffectedClustersLastEvaluatedKey | null>(null);
    const fetchedAtLeastOnePageRef = useRef<boolean>(false);

    // Accumulated urls returned from the hook. for load more list after the 5 sample urls
    const [accumulatedUrls, setAccumulatedUrls] = useState<ServiceableUrl[]>([]);

    const { isRunningBatchAnalysis } = useBatchUrlAnalysisProcessesPolling(websiteGuid, false);

    const [fetchAffectedUrlsPage, { isFetching: isFetchingAffectedUrlsNextPage }] = useLazyFetchAffectedUrlsQuery();

    const fetchPage = async (lastEvaluatedKey: AffectedClustersLastEvaluatedKey) => {
        const fetchUrlsPromise = fetchAffectedUrlsPage(
            {
                w_guid: websiteGuid,
                cluster_guid: clusterGuid,
                seo_test_key: seoTestKey,
                limit: SERVICEABLE_URLS_ON_PAGE,
                last_evaluated_key: getUrlStringFromObjectEntries(lastEvaluatedKey),
            },
            !isRunningBatchAnalysis
        );

        fetchUrlsPromise.unsubscribe();
        const nextPage = await fetchUrlsPromise.unwrap();
        return nextPage;
    };

    // On load more fetch next page and add to accumulated urls
    const handleLoadMore = async () => {
        if (lastEvaluatedKey || clusterLastEvaluatedKey) {
            try {
                const nextPage = await fetchPage(lastEvaluatedKey || clusterLastEvaluatedKey!);

                fetchedAtLeastOnePageRef.current = true;
                setAccumulatedUrls([...accumulatedUrls, ...nextPage.data]);
                setLastEvaluatedKey(nextPage.meta.last_evaluated_key);
            } catch (error) {}
        }
    };

    const hasFirstEvaluatedKey = fetchedAtLeastOnePageRef.current === false && clusterLastEvaluatedKey !== null;

    return {
        accumulatedUrls,
        isFetchingAffectedUrlsNextPage,
        hasMore: lastEvaluatedKey !== null || hasFirstEvaluatedKey,
        handleLoadMore,
    };
}
